import { Header, Text } from '@/components/common/Typography';
import { DepositoryAccountSelector } from '@/components/financialEntities/DepositoryAccountSelectionView';
import { useCheckDepositoryAccount } from '@/components/financialEntities/helpers';
import { FlexProps } from '@chakra-ui/react';
import { assert } from '@common/errors';
import { GoalAccountOnboardingStep } from '@common/types';
import { useState } from 'react';
import { useGoalOnboardingContext } from './GoalOnboardingContext';
import GoalOnboardingLayout from './GoalOnboardingLayout';

export interface GoalOnboardingConnectProps extends FlexProps {
    onComplete: (step?: GoalAccountOnboardingStep) => void;
    onBack: () => void;
}

const GoalOnboardingConnect = ({
    onComplete,
    onBack,
    ...props
}: GoalOnboardingConnectProps) => {
    const { goalOnboardingData, isUpdating, updateGoalOnboarding } =
        useGoalOnboardingContext();
    const [selectedAccountId, setSelectedAccountId] = useState<
        string | undefined
    >(goalOnboardingData.fundingAccountId ?? undefined);
    const { checkDepositoryAccount, errorMessageByAccountId } =
        useCheckDepositoryAccount({
            setSelectedAccountId: setSelectedAccountId,
        });
    const [isSubmitting, setIsSubmitting] = useState(false);

    const submit = async () => {
        assert(selectedAccountId);
        if (isSubmitting) return;
        setIsSubmitting(true);
        try {
            await checkDepositoryAccount(selectedAccountId);
            await updateGoalOnboarding({
                params: {
                    info: {
                        fundingAccountId: selectedAccountId,
                        lastCompletedStep: 'CONNECT',
                    },
                },
            });
            onComplete();
        } catch {
            // do nothing; allow checkDepositoryAccount to handle errors
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <GoalOnboardingLayout
            {...props}
            footer={{
                cta: {
                    text: 'Next',
                    onComplete: submit,
                    isSubmitting: isSubmitting || isUpdating,
                    isDisabled:
                        isSubmitting || isUpdating || !selectedAccountId,
                },
                back: { onBack },
            }}
        >
            <Header fontWeight="medium" mb={2}>
                Choose the source account to fund your investment goal
            </Header>
            <Text color="textMuted" mb={10}>
                Choose a checking or savings account to get started; you can
                always change the account in the future.
            </Text>
            <DepositoryAccountSelector
                selectedAccountId={selectedAccountId}
                onChangeSelectedAccountId={setSelectedAccountId}
                errorMessageByAccountId={errorMessageByAccountId}
                isDisabled={isSubmitting}
            />
        </GoalOnboardingLayout>
    );
};

export default GoalOnboardingConnect;
